import { isAfter, isBefore, setMilliseconds, setSeconds } from "date-fns";
import { isNil } from "lodash-es"; // Make sure to import isNil if it's used

/**
 * Validates if a given timestamp is after a specified process start time.
 * @param timestamp The timestamp to validate.
 * @param processStartTime The process start time to compare against.
 * @returns True if the timestamp is valid, or an error message string otherwise.
 */
export const validateTimestamp = (
  timestamp: Date,
  processStartTime: Date | undefined,
): true | string => {
  if (!processStartTime) {
    return true; // No process start time to compare against, so it's valid
  }

  const userTimestampNormalized = setMilliseconds(setSeconds(timestamp, 0), 0);
  const processStartNormalized = setMilliseconds(
    setSeconds(processStartTime, 0),
    0,
  );

  if (isBefore(userTimestampNormalized, processStartNormalized)) {
    return "Timestamp must be after process Start";
  }

  const nowNormalized = setMilliseconds(setSeconds(new Date(), 0), 0);
  if (isAfter(userTimestampNormalized, nowNormalized)) {
    return "Timestamp cannot be in the future";
  }

  return true;
};

/**
 * Validates if a given temperature value is within a specified range.
 * @param value The temperature value to validate.
 * @param minTemp The minimum allowed temperature.
 * @param maxTemp The maximum allowed temperature.
 * @returns True if the temperature is valid, or an error message string otherwise.
 */
export const validateTemperature = (
  value: number | null,
  minTemp: number,
  maxTemp: number,
): true | string => {
  if (isNil(value)) {
    return "Temperature is required";
  }
  if (value < minTemp || value > maxTemp) {
    return `Temperature must be between ${minTemp} and ${maxTemp}.`;
  }
  return true;
};
